import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
// Antes esto se suscribía a `useReadCartaProgress` (SignalR push).
// Ahora TaskMicro ejecuta el scan y NO tiene hub — el progress viene
// via prop desde el DTO del polling REST (`GET /jobs/{id}.progress`).

/**
 * Panel de progreso realtime del escaneo IA. Se suscribe al hub SignalR
 * cuando `enabled=true` y pinta:
 *   • Fase actual (single-call / chunk-processing / merge / consolidation).
 *   • Barra de progreso agregada (chunks completados).
 *   • Lista de chunks con estado (pending / processing / done).
 *   • Detecciones acumuladas (nombres de productos pintados en vivo).
 *
 * Cuando `enabled=false` retorna null (no pinta nada). El padre lo
 * activa mientras `scanBusy=true`.
 */

const PHASE_LABEL = {
  idle: "Preparando…",
  "single-call": "Analizando carta con IA…",
  "job-start": "Preparando escaneo…",
  "chunk-start": "Procesando archivos en paralelo…",
  "chunk-complete": "Procesando archivos en paralelo…",
  "merge-done": "Fusionando resultados…",
  "consolidation-start": "Revisando consistencia con IA…",
  "job-complete": "¡Escaneo completado!",
  "job-error": "Error en el escaneo",
};

export default function ScanProgressPanel({ enabled, progress: progressProp }) {
  if (!enabled) return null;

  // Progress viene del padre — es el `progress` deserializado que
  // devuelve `GET /ReadCarta/claude/jobs/{id}`. Shape esperado:
  // { phase, totalPages, totalChunks, chunkIndex, chunkSize,
  //   categoriesFound, productsFound, sampleNames[] }.
  // Cuando no hay dato aún (fase inicial pre-primer-chunk) mostramos
  // un progreso indeterminado con "Preparando…".
  const p = progressProp || {};
  const phase = p.phase || "idle";
  // Backend usa `totalPages` en job-start y `pages` en single-call —
  // aceptamos ambos para que la fase inicial muestre el contador.
  const totalPages = p.totalPages || p.pages || 0;
  const totalChunks = p.totalChunks || 0;
  const doneChunks = typeof p.chunkIndex === "number"
    ? (phase === "chunk-complete" ? p.chunkIndex + 1 : p.chunkIndex)
    : 0;
  const error = p.error || null;

  // Con polling no tenemos el detalle chunk-by-chunk como en SignalR.
  // El TaskMicro emite el último snapshot; si venía en formato flat
  // (chunkIndex + categoriesFound + productsFound + sampleNames),
  // lo mostramos como resumen agregado. Sin lista de chunks individual.
  const chunks = Array.isArray(p.chunks) ? p.chunks : [];
  const mergedProducts = p.mergedProducts || 0;
  const finalProducts = p.finalProducts || 0;
  const categoriesFound = p.categoriesFound || 0;
  const productsFound = p.productsFound || 0;
  const allSampleNames = Array.isArray(p.sampleNames)
    ? p.sampleNames
    : (Array.isArray(p.allSampleNames) ? p.allSampleNames : []);

  const progressPct = totalChunks > 0
    ? (doneChunks / totalChunks) * 100
    : phase === "single-call" ? 30 : 0;
  const isConsolidating = phase === "consolidation-start";
  const isMerging = phase === "merge-done";
  const isDone = phase === "job-complete";
  const isError = phase === "job-error";
  const isSingleCall = phase === "single-call";

  const totalDetected =
    isDone && finalProducts
      ? finalProducts
      : (mergedProducts || productsFound || 0);
  const totalCategories = categoriesFound || 0;

  return (
    <Box>
      {/* Estado principal */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {isError ? (
          <ErrorRoundedIcon sx={{ color: "error.main", fontSize: 40 }} />
        ) : isDone ? (
          <CheckCircleRoundedIcon sx={{ color: "success.main", fontSize: 40 }} />
        ) : (
          <CircularProgress size={32} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {PHASE_LABEL[phase] || "Procesando…"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isSingleCall &&
              `Analizando ${totalPages || 0} archivo(s) con IA…`}
            {(phase === "chunk-start" || phase === "chunk-complete") &&
              `${doneChunks} de ${totalChunks} páginas completadas`}
            {isMerging && `Fusionando duplicados…`}
            {isConsolidating &&
              `Revisando duplicados y matching con tu catálogo existente…`}
            {isDone && `${totalDetected} producto(s) listos para revisar`}
            {isError && (error || "Fallo desconocido")}
          </Typography>
        </Box>
      </Stack>

      {/* Barra de progreso */}
      {!isError && (
        <LinearProgress
          variant={isConsolidating || isMerging || isSingleCall ? "indeterminate" : "determinate"}
          value={progressPct}
          sx={{ height: 8, borderRadius: 4, mb: 2 }}
          color={isDone ? "success" : "primary"}
        />
      )}

      {/* Contadores destacados — siempre que haya progreso detectado.
          Antes solo aparecía dentro del texto "N producto(s) detectado(s)"
          y se perdía visualmente. Con chips grandes el usuario ve el
          crecimiento del scan en vivo. */}
      {!isError && !isDone && (totalDetected > 0 || totalCategories > 0) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label={`${totalCategories} categoría${totalCategories === 1 ? "" : "s"}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={`${totalDetected} producto${totalDetected === 1 ? "" : "s"}`}
            color="success"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      )}

      {/* Chunks individuales — solo si hay > 1 (multi-chunk path) */}
      {totalChunks > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
            Bloques
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {chunks.map((c) => (
              <Stack
                key={c.index}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  bgcolor:
                    c.status === "done"
                      ? "success.lighter"
                      : c.status === "processing"
                      ? "primary.lighter"
                      : "background.neutral",
                }}
              >
                {c.status === "done" ? (
                  <CheckCircleRoundedIcon fontSize="small" sx={{ color: "success.main" }} />
                ) : c.status === "processing" ? (
                  <HourglassBottomRoundedIcon fontSize="small" sx={{ color: "primary.main" }} />
                ) : (
                  <RadioButtonUncheckedRoundedIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                )}
                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 78 }}>
                  Bloque {c.index + 1}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.status === "done" && c.sampleNames?.length > 0
                    ? c.sampleNames.slice(0, 3).join(" · ") +
                      (c.productsFound > 3 ? ` · +${c.productsFound - 3} más` : "")
                    : c.status === "processing"
                    ? "Analizando con IA…"
                    : "En espera"}
                </Typography>
                {c.status === "done" && (
                  <Chip
                    size="small"
                    label={`${c.productsFound}`}
                    color="success"
                    sx={{ height: 20, fontWeight: 800 }}
                  />
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {/* Detecciones en vivo — nombres acumulados de todos los chunks */}
      {allSampleNames.length > 0 && !isDone && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
            Detectado en tiempo real
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 0.75,
              maxHeight: 120,
              overflow: "auto",
            }}
          >
            {allSampleNames.map((name, idx) => (
              <Chip
                key={`${name}-${idx}`}
                size="small"
                label={name}
                icon={<BoltRoundedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontWeight: 600,
                  bgcolor: "primary.lighter",
                  color: "primary.darker",
                  animation: "fadeIn 0.3s",
                  "@keyframes fadeIn": {
                    from: { opacity: 0, transform: "translateY(-4px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Nota anti-ansiedad si consolidando (etapa que tarda) */}
      {isConsolidating && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 2, color: "text.disabled", fontStyle: "italic" }}
        >
          Casi listo — la IA está revisando todo por segunda vez para dejar
          la carta lo más limpia posible…
        </Typography>
      )}
    </Box>
  );
}

ScanProgressPanel.propTypes = {
  enabled: PropTypes.bool.isRequired,
  progress: PropTypes.object,
};
